import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

const Accordion = AccordionPrimitive.Root

const AccordionItem = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
  ref?: Ref<ComponentRef<typeof AccordionPrimitive.Item>>
}) => <AccordionPrimitive.Item ref={ref} className={cn('tw:border-b', className)} {...props} />

type JustifyOptions = 'center' | 'between'

type AccordionTriggerProps = ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  justify?: JustifyOptions
  ref?: Ref<ComponentRef<typeof AccordionPrimitive.Trigger>>
}

const AccordionTrigger = ({ className, children, justify = 'between', ref, ...props }: AccordionTriggerProps) => {
  const justifyClass = justify === 'center' ? 'tw:justify-center' : 'tw:justify-between'

  return (
    <AccordionPrimitive.Header className="tw:flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'tw:flex tw:flex-1 tw:items-center tw:py-4 tw:text-sm tw:font-medium tw:transition-all tw:hover:underline tw:text-left',
          'tw:[&[data-state=open]>svg]:rotate-180',
          justifyClass,
          className
        )}
        {...props}
      >
        {children}
        <Icon
          className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-cool-500 tw:transition-transform tw:duration-200"
          type="chevron-down"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

const AccordionContent = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof AccordionPrimitive.Content>>
}) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="tw:overflow-hidden tw:text-sm tw:data-[state=closed]:h-0 tw:data-[state=closed]:animate-accordion-up tw:data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn('tw:pb-4 tw:pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
)

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
